import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  TextField,
  Box,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../store/cartSlice';
import { fetchProducts } from '../store/slices/productsSlice';
import { fetchCarouselImages } from '../features/carousel/carouselSlice';
import WhatsAppButton from '../components/WhatsAppButton';
import { typographyStyles } from '../styles/commonStyles';

export default function Products() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { images: bannerImages, status: bannerStatus } = useSelector((state) => state.carousel);
  const { items: products, status: productsStatus } = useSelector((state) => state.products);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [page, setPage] = useState(0);
  const [itemsPerPage] = useState(12);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch banner images
  useEffect(() => {
    dispatch(fetchCarouselImages());
  }, [dispatch]);

  // Fetch products when search/category changes
  useEffect(() => {
    console.log('Fetching products with params:', {
      search: searchTerm,
      skip: page * itemsPerPage,
      limit: itemsPerPage,
      category: selectedCategory === 'all' ? undefined : selectedCategory
    });
    
    dispatch(fetchProducts({
      search: searchTerm,
      skip: page * itemsPerPage,
      limit: itemsPerPage,
      category: selectedCategory === 'all' ? undefined : selectedCategory
    }));
  }, [dispatch, searchTerm, selectedCategory, page, itemsPerPage]);

  // Auto-play banner
  useEffect(() => {
    let intervalId;
    if (isAutoPlaying && bannerImages?.length > 0) {
      intervalId = setInterval(() => {
        setCurrentBannerIndex((prevIndex) => 
          prevIndex === bannerImages.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000); // Change slide every 5 seconds
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAutoPlaying, bannerImages]);

  // Pause auto-play on hover
  const handleBannerHover = (isHovering) => {
    setIsAutoPlaying(!isHovering);
  };

  const handlePrevBanner = () => {
    setCurrentBannerIndex((prevIndex) => 
      prevIndex === 0 ? bannerImages.length - 1 : prevIndex - 1
    );
  };

  const handleNextBanner = () => {
    setCurrentBannerIndex((prevIndex) => 
      prevIndex === bannerImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleDotClick = (index) => {
    setCurrentBannerIndex(index);
  };

  const handleAddToCart = (product) => {
    dispatch(addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url,
      quantity: 1
    }));
    setOpenSnackbar(true);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setPage(0); // Reset to first page when changing category
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  // Content sections
  const renderSearchAndFilters = () => (
    <Box sx={{ mb: 4 }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder={t('products.searchPlaceholder')}
        value={searchTerm}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ mb: 2 }}
      />
      <Tabs
        value={selectedCategory}
        onChange={handleCategoryChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="product categories"
      >
        <Tab value="all" label={t('products.filters.all')} />
        <Tab value="velas" label={t('products.filters.velas')} />
        <Tab value="sahumerios" label={t('products.filters.sahumerios')} />
        <Tab value="otros" label={t('products.filters.otros')} />
      </Tabs>
    </Box>
  );

  // Products Grid
  const renderProducts = () => {
    if (productsStatus === 'loading') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (productsStatus === 'error') {
      return (
        <Box sx={{ width: '100%', mt: 4 }}>
          <Alert severity="error">{t('errors.fetchProducts')}</Alert>
        </Box>
      );
    }

    if (!products || products.length === 0) {
      return (
        <Box sx={{ width: '100%', mt: 4, textAlign: 'center' }}>
          <Typography variant="h6">{t('products.noProducts')}</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={4}>
        {products.map((product) => (
          <Grid item key={product.id} xs={12} sm={6} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: product.id * 0.1 }}
              style={{ height: '100%' }}
            >
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  minHeight: { xs: '450px', sm: '500px' }, 
                  maxHeight: { xs: '450px', sm: '500px' }  
                }}
              >
                <CardMedia
                  component="div"
                  sx={{
                    height: '250px', 
                    backgroundColor: 'rgba(139, 35, 49, 0.1)',
                    backgroundImage: `url(${product.image_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <CardContent sx={{ 
                  flexGrow: 1, 
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}>
                  <Typography
                    gutterBottom
                    component="h2"
                    sx={{
                      ...typographyStyles.smallTitle,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {product.name}
                  </Typography>
                  <Typography 
                    sx={{
                      ...typographyStyles.bodyText,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {product.description}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mt: 'auto' }}>
                    ${product.price}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={t(`products.filters.${product.category}`)}
                      size="small"
                      color="secondary"
                    />
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    size="small"
                    onClick={() => handleProductClick(product)}
                  >
                    {t('products.viewDetails')}
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => handleAddToCart(product)}
                  >
                    {t('products.addToCart')}
                  </Button>
                </CardActions>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
      {/* Banner Carousel */}
      <Box 
        sx={{ position: 'relative', mb: 4, overflow: 'hidden' }}
        onMouseEnter={() => handleBannerHover(true)}
        onMouseLeave={() => handleBannerHover(false)}
      >
        {bannerStatus === 'loading' ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress />
          </Box>
        ) : bannerImages && bannerImages.length > 0 ? (
          <Box
            sx={{
              position: 'relative',
              height: { xs: '200px', sm: '300px', md: '400px' },
              width: '100%',
              overflow: 'hidden'
            }}
          >
            {bannerImages.map((banner, index) => (
              <motion.div
                key={banner.id}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: index === currentBannerIndex ? 1 : 0,
                  x: `${(index - currentBannerIndex) * 100}%`,
                  zIndex: index === currentBannerIndex ? 1 : 0
                }}
                transition={{ duration: 0.5 }}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${banner.image_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 3,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                      color: 'white',
                    }}
                  >
                    {banner.title && (
                      <Typography variant="h5" sx={{ mb: 1 }}>{banner.title}</Typography>
                    )}
                    {banner.description && (
                      <Typography variant="body1">{banner.description}</Typography>
                    )}
                  </Box>
                </Box>
              </motion.div>
            ))}
            {/* Navigation Arrows */}
            <IconButton
              onClick={handlePrevBanner}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.3)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
                color: 'white',
                zIndex: 2
              }}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
            <IconButton
              onClick={handleNextBanner}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.3)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
                color: 'white',
                zIndex: 2
              }}
            >
              <KeyboardArrowRightIcon />
            </IconButton>

            {/* Dots Indicator */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
                zIndex: 2
              }}
            >
              {bannerImages.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => handleDotClick(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: index === currentBannerIndex ? 'primary.main' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'scale(1.2)',
                      bgcolor: 'primary.light'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h6">No banner images found.</Typography>
          </Box>
        )}
      </Box>

      {/* Search and Filter Section */}
      {renderSearchAndFilters()}

      {/* Products Grid */}
      {renderProducts()}

      {/* Product Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {selectedProduct && (
          <>
            <DialogTitle>
              {selectedProduct.name}
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <img
                  src={selectedProduct.image_url}
                  alt={selectedProduct.name}
                  style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                />
              </Box>
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={typographyStyles.smallTitle}
              >
                ${selectedProduct.price}
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={typographyStyles.bodyText}
              >
                {selectedProduct.description}
              </Typography>
              <Typography
                variant="h6"
                gutterBottom
                sx={typographyStyles.smallTitle}
              >
                {t('products.details.energy')}
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={typographyStyles.bodyText}
              >
                {selectedProduct.energy_properties}
              </Typography>
              <Typography
                variant="h6"
                gutterBottom
                sx={typographyStyles.smallTitle}
              >
                {t('products.details.flowers')}
              </Typography>
              <List>
                {selectedProduct.flowers.map((flower, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={flower} />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                {t('common.close')}
              </Button>
              <Button 
                onClick={() => {
                  handleAddToCart(selectedProduct);
                  handleCloseDialog();
                }}
                color="primary"
                variant="contained"
              >
                {t('products.addToCart')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add to Cart Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {t('products.addedToCart')}
        </Alert>
      </Snackbar>

      {/* WhatsApp Button */}
      <WhatsAppButton />
    </Container>
  );
}
